/**
 * Fichier : chat_widget/src/widget/hooks/useChatForms.ts
 *
 * Principe :
 * Ce hook isole toute la logique liée aux formulaires conversationnels
 * (demande d'informations, prise de rendez-vous, etc.) dans le widget.
 *
 * Il gère :
 * - l'état du formulaire actif (formActive)
 * - l'identifiant du véhicule associé au formulaire (formCarId)
 * - l'état du véhicule attaché à la conversation (attachedCar)  ✅ PERSISTÉ
 * - la persistance dans le localStorage, scoped par (userId + sessionId)
 *
 * L'objectif est de garder useChatPanel léger et d'éviter toute logique
 * de persistance directement dans le hook principal du chat.
 */
import { useEffect, useMemo, useState } from "react";
import type { Vehicle } from "../types/VehicleTypes";
import type { FormActiveType } from "../types/formTypes";

export function useChatForms({
  effectiveUserId,
  sessionId,
}: {
  effectiveUserId: string | null;
  sessionId: string;
}) {
  const userKey = effectiveUserId || "anon";

  const keys = useMemo(() => {
    return {
      formCarIdKey: `vcw_formCarId_${userKey}_${sessionId}`,
      formActiveKey: `vcw_formActive_${userKey}_${sessionId}`,
      attachedCarKey: `vcw_attachedCar_${userKey}_${sessionId}`, // ✅ NEW
    };
  }, [userKey, sessionId]);

  const [formCarId, setFormCarId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(keys.formCarIdKey);
    } catch {
      return null;
    }
  });

  const [formActive, setFormActive] = useState<FormActiveType | null>(() => {
    try {
      const object = localStorage.getItem(keys.formActiveKey);
      return object ? JSON.parse(object) : null;
    } catch {
      return null;
    }
  });

  // ✅ NEW: attachedCar persisted
  const [attachedCar, setAttachedCar] = useState<Vehicle | null>(() => {
    try {
      const raw = localStorage.getItem(keys.attachedCarKey);
      if (!raw) return null;
      return JSON.parse(raw) as Vehicle;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (formCarId) localStorage.setItem(keys.formCarIdKey, formCarId);
      else localStorage.removeItem(keys.formCarIdKey);
    } catch {
      // silent
    }
  }, [formCarId, keys.formCarIdKey]);

  useEffect(() => {
    try {
      if (formActive)
        localStorage.setItem(keys.formActiveKey, JSON.stringify(formActive));
      else localStorage.removeItem(keys.formActiveKey);
    } catch {
      // silent
    }
  }, [formActive, keys.formActiveKey]);

  // ✅ NEW persistence
  useEffect(() => {
    try {
      if (attachedCar) {
        localStorage.setItem(keys.attachedCarKey, JSON.stringify(attachedCar));
      } else {
        localStorage.removeItem(keys.attachedCarKey);
      }
    } catch {
      // silent
    }
  }, [attachedCar, keys.attachedCarKey]);

  const resetForms = () => {
    setFormActive(null);
    setFormCarId(null);
    setAttachedCar(null);
  };

  return {
    formActive,
    formCarId,
    setFormActive,
    setFormCarId,

    // ✅ NEW API
    attachedCar,
    setAttachedCar,
    resetForms,
  };
}
